import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DetailPageTemplate from '../components/templates/DetailPageTemplate';
import HeroSection from '../components/organisms/HeroSection';
import SectionHeader from '../components/molecules/SectionHeader';
import FeatureCard from '../components/molecules/FeatureCard';
import ServiceCard from '../components/molecules/ServiceCard';
import Button from '../components/atoms/Button';
import Text from '../components/atoms/Text';

const ChibaGourmetDetail = () => {
  const { t } = useTranslation();

  // 翻訳されたデータを使用
  const features = t('serviceDetail.chibaGourmet.features.list', { returnObjects: true });

  const tourCourses = t('serviceDetail.chibaGourmet.tourCourses', { returnObjects: true });

  const seasonalHighlights = t('serviceDetail.chibaGourmet.seasonalHighlights', { returnObjects: true });

  // 各データがArray型かチェック
  const isValidFeatures = Array.isArray(features);
  const isValidTourCourses = Array.isArray(tourCourses);
  const isValidSeasonalHighlights = Array.isArray(seasonalHighlights);

  return (
    <DetailPageTemplate
      serviceName={t('serviceDetail.chibaGourmet.title')}
      onContactSubmit={(data) => {
        console.log('Chiba Gourmet inquiry:', data);
        alert(t('serviceDetail.chibaGourmet.inquirySuccess'));
      }}
    >
      {({ onContactClick }) => (
        <>
          {/* Hero Section */}
          <HeroSection
            badge={t('serviceDetail.chibaGourmet.badge')}
            title={t('serviceDetail.chibaGourmet.title')}
            subtitle={t('serviceDetail.chibaGourmet.subtitle')}
            description={t('serviceDetail.chibaGourmet.description')}
            primaryAction={{
              text: t('serviceDetail.chibaGourmet.primaryAction'),
              onClick: onContactClick
            }}
            secondaryAction={{
              text: t('serviceDetail.chibaGourmet.secondaryAction'),
              onClick: () => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })
            }}
          />

          {/* Service Features */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.chibaGourmet.features.badge')}
                title={t('serviceDetail.chibaGourmet.features.title')}
                subtitle={t('serviceDetail.chibaGourmet.features.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {isValidFeatures ? features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                )) : null}
              </div>
            </div>
          </section>

          {/* Tour Courses */}
          <section id="courses" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.chibaGourmet.courses.badge')}
                title={t('serviceDetail.chibaGourmet.courses.title')}
                subtitle={t('serviceDetail.chibaGourmet.courses.subtitle')}
                className="mb-16"
              />

              <div className="space-y-8">
                {tourCourses.map((course, index) => (
                  <ServiceCard
                    key={index}
                    title={course.title}
                    description={course.description}
                    duration={course.duration}
                    price={course.price}
                    spots={course.spots}
                    image={t('serviceDetail.chibaGourmet.tourImage')}
                    onBooking={onContactClick}
                    onInquire={onContactClick}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Seasonal Highlights */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.chibaGourmet.seasonal.badge')}
                title={t('serviceDetail.chibaGourmet.seasonal.title')}
                subtitle={t('serviceDetail.chibaGourmet.seasonal.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {Array.isArray(seasonalHighlights) ? seasonalHighlights.map((season, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">{season.season}</h3>
                    <Text className="mb-4">{season.highlights}</Text>
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <Text size="sm" weight="medium" color="orange-700">
                        {t('serviceDetail.chibaGourmet.seasonalRecommended')}
                      </Text>
                      <Text size="sm" color="orange-600">
                        {season.bestExperience}
                      </Text>
                    </div>
                  </div>
                )) : null}
              </div>
            </div>
          </section>

          {/* Experience Gallery */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.chibaGourmet.gallery.badge')}
                title={t('serviceDetail.chibaGourmet.gallery.title')}
                subtitle={t('serviceDetail.chibaGourmet.gallery.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array.isArray(t('serviceDetail.chibaGourmet.galleryItems', { returnObjects: true })) ?
                  t('serviceDetail.chibaGourmet.galleryItems', { returnObjects: true }).map((item, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="h-48 bg-gray-200 flex items-center justify-center">
                        <Text color="gray-500">{item.title}{t('serviceDetail.chibaGourmet.galleryPhoto')}</Text>
                      </div>
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">{item.title}</h3>
                        <Text size="sm">{item.description}</Text>
                      </div>
                    </div>
                  )) : null}
              </div>
            </div>
          </section>

          {/* Safety & Quality */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.chibaGourmet.safety.badge')}
                title={t('serviceDetail.chibaGourmet.safety.title')}
                subtitle={t('serviceDetail.chibaGourmet.safety.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {Array.isArray(t('serviceDetail.chibaGourmet.safetyItems', { returnObjects: true })) ?
                  t('serviceDetail.chibaGourmet.safetyItems', { returnObjects: true }).map((item, index) => (
                    <FeatureCard
                      key={index}
                      icon={item.icon}
                      title={item.title}
                      description={item.description}
                    />
                  )) : null}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-orange-500 text-white">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-4xl font-bold mb-8">
                {t('serviceDetail.chibaGourmet.cta.title')}
              </h2>
              <Text size="xl" color="white" className="opacity-90 max-w-3xl mx-auto mb-12">
                {t('serviceDetail.chibaGourmet.cta.description')}
              </Text>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="large"
                  onClick={onContactClick}
                  className="bg-white text-orange-500 hover:bg-gray-100"
                >
                  {t('serviceDetail.chibaGourmet.cta.primaryAction')}
                </Button>
                <Link
                  to="/"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-orange-500 transition-all duration-200 inline-flex items-center justify-center"
                >
                  {t('serviceDetail.chibaGourmet.cta.secondaryAction')}
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </DetailPageTemplate>
  );
};
export default ChibaGourmetDetail;
